import * as React from 'react';
import { Translate } from 'react-localize-redux-dep-updated';
import { useDispatch } from 'react-redux';
import {
  Heading,
  InputSwitch,
  SectionWrapper,
  SheetLayoutContent,
  SheetLayoutHeader,
} from '../../../../brenger-shared-ui';
import { useTypedSelector } from '../../../../hooks';
import { useVatNumberValidator } from '../../../../hooks/useVatNumberValidator';
import { getCrossDomainSetup } from '../../../../utils/basics';
import { getBasePath } from '../../../../utils/global';
import { validateDutchPostalCode, validateEmail, validateLine1, validatePhone } from '../../../../utils/validation';
import { isUserLoggedIn } from '../../../User/ducks';
import FooterLayout from '../../containers/FooterLayout';
import { actions, isContactContinueButtonDisabled } from '../../ducks';
import { proceed } from '../../proceed';
import { BusinessFields } from './BusinessFields';
import { ContactFields } from './ContactFields';

export const ContactCustomer: React.FC = () => {
  const isLoggedIn = useTypedSelector(isUserLoggedIn);
  const crossDomain = getCrossDomainSetup();
  const customerDetails = useTypedSelector(state => state.generalTransport.contact.customer?.contact_details);
  const companyDetails = useTypedSelector(state => state.generalTransport.contact.customer?.company_details);
  const isBusiness = !!useTypedSelector(state => state.generalTransport.contact.customer?.is_business);
  const isContinueDisabled = useTypedSelector(isContactContinueButtonDisabled);

  const dispatch = useDispatch();
  const [forceShowErrors, setForceShowErrors] = React.useState(false);
  const { isVatLoading, isVatValid } = useVatNumberValidator(companyDetails?.vat_number || '');

  const ConsumerTypeOptions: {
    value: boolean;
    label: React.ReactNode;
  }[] = [
    {
      value: false,
      label: <Translate id={'request_flow.contact.is_private'} />,
    },
    {
      value: true,
      label: <Translate id={'request_flow.contact.is_business'} />,
    },
  ];

  const submit = (): void => {
    // When trying to submit, but it isn't possible yet: force show errors
    setForceShowErrors(true);

    if (
      !customerDetails?.first_name ||
      !customerDetails?.last_name ||
      validatePhone(customerDetails?.phone) ||
      validateEmail(customerDetails?.email)
    ) {
      return;
    }
    if (isBusiness && isVatLoading) return;
    if (isBusiness) {
      const isPostalCodeValid =
        companyDetails?.address?.country_code === 'NL'
          ? !validateDutchPostalCode(companyDetails?.address?.postal_code)
          : !!companyDetails?.address?.postal_code;
      // It is ok to not fill in VAT, but if filled in then it should be valid
      const isVatNumberValid = !companyDetails?.vat_number || isVatValid;
      const hasError = !isPostalCodeValid || !isVatNumberValid || !!validateLine1(companyDetails?.address?.line1);
      const hasMissing = !companyDetails?.address?.locality || !companyDetails?.address?.country_code;

      if (hasError || hasMissing) return;
    }
    proceed();
  };

  return (
    <>
      <SheetLayoutHeader>
        <Heading size={2}>
          <Translate id={'request_flow.headers.contact_main'} />
        </Heading>
        {/* Cross domain logins don't work, so we don't offer login here, this wouldl ead to a bad experience */}
        {!crossDomain && (
          <p className="mt-1">
            <Translate id={'request_flow.headers.contact_sub'} />
            {!isLoggedIn && (
              <>
                {' '}
                <Translate id={'authentication.login.brief'} />
                <a href={`${getBasePath()}/login?contact=true`} className="underline">
                  <Translate id={'authentication.login.button'} />
                </a>
              </>
            )}
          </p>
        )}
      </SheetLayoutHeader>
      <SheetLayoutContent>
        <InputSwitch
          id={'customer_type'}
          value={isBusiness}
          options={ConsumerTypeOptions}
          onChange={(v: boolean) => {
            dispatch(actions.setContactIsBusiness(v));
            setForceShowErrors(false);
          }}
        />
      </SheetLayoutContent>
      <SectionWrapper centerContent={false} sectionStyle={'primary-light'} dashed={true}>
        <SheetLayoutContent>
          <ContactFields type="customer" forceShowErrors={forceShowErrors} />
        </SheetLayoutContent>
      </SectionWrapper>
      <SheetLayoutContent>
        {isBusiness && (
          <>
            <div className="pb-[1em]" />
            <Heading size={4} extraClasses="mb-2">
              <Translate id={'request_flow.contact.business_details'} />
            </Heading>
            <BusinessFields forceShowErrors={forceShowErrors} />
          </>
        )}
      </SheetLayoutContent>
      <FooterLayout customSubmitFunction={submit} customIsDisabled={isContinueDisabled} />
    </>
  );
};
